import { useState, useEffect, useCallback } from 'react';
import { buscarRecomendacoes, agruparPorColecao, agruparEEmbaralhar, agruparComFallbackEmbaralhar } from '../utils/recomendacoes';
import { buscarMeditacoes } from '../utils/meditationsContent';
import LivroDoMes from '../components/LivroDoMes';
import { buscarLivroDoMes } from '../utils/livros';

// ── URL da planilha unificada ─────────────────────────────────────────────────
var CSV_URL = 'https://docs.google.com/spreadsheets/d/1-DexkhRwRrEamowes1qUirTAOF1BlE8AuyG_O70T2m8/export?format=csv&gid=2147167278';

// ── Labels e emojis por tipo ─────────────────────────────────────────────────
var TIPO_CONFIG = {
  livro:     { emoji: '📚', label: 'Livro',     cta: 'Comprar',     cor: '#e8e4f0', corTexto: '#2f2b5f' },
  produto:   { emoji: '🛍️', label: 'Produto',   cta: 'Ver produto', cor: '#e8f5f0', corTexto: '#2e7d5c' },
  filme:     { emoji: '🎬', label: 'Filme',     cta: 'Assistir',    cor: '#e8f0f5', corTexto: '#1a4a6b' },
  youtube:   { emoji: '▶️', label: 'Vídeo',    cta: 'Assistir',    cor: '#fce4ec', corTexto: '#b71c1c' },
  meditacao: { emoji: '🧘', label: 'Meditação', cta: null,          cor: '#f3e5f5', corTexto: '#6a1b9a' },
};

var COR_NIVEL = {
  iniciante:     { bg: '#e8f5e9', color: '#2e7d32' },
  facil:         { bg: '#e8f5e9', color: '#2e7d32' },
  intermediario: { bg: '#fff3e0', color: '#e65100' },
  avancado:      { bg: '#fce4ec', color: '#c62828' },
};

function normalizarNivel(nivel) {
  return (nivel || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function nivelStyle(nivel) {
  return COR_NIVEL[normalizarNivel(nivel)] || { bg: 'var(--primary-light)', color: 'var(--primary)' };
}

function nivelLabel(nivel) {
  var key = normalizarNivel(nivel);
  if (key === 'iniciante' || key === 'facil') return 'Fácil';
  return nivel;
}

// ── Pill de tipo ─────────────────────────────────────────────────────────────
function TipoPill({ tipo, small }) {
  var cfg = TIPO_CONFIG[tipo] || {};
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: small ? '0.65rem' : '0.72rem',
      background: cfg.cor || '#eee', color: cfg.corTexto || '#333',
      borderRadius: 'var(--r-full)', padding: small ? '1px 7px' : '3px 10px',
      fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      {cfg.emoji} {cfg.label}
    </span>
  );
}

function NivelPill({ nivel, small }) {
  if (!nivel) return null;
  var ns = nivelStyle(nivel);
  return (
    <span style={{
      fontSize: small ? '0.65rem' : '0.72rem',
      background: ns.bg, color: ns.color,
      borderRadius: 'var(--r-full)', padding: small ? '1px 7px' : '3px 10px',
      fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      {nivelLabel(nivel)}
    </span>
  );
}

// ── Modal de item ─────────────────────────────────────────────────────────────
function ItemModal({ item, onFechar }) {
  var [imgErro, setImgErro] = useState(false);
  var cfg = TIPO_CONFIG[item.tipo] || {};
  var temLink = item.link && item.link.indexOf('http') === 0;
  var eMidia = item.tipo === 'meditacao' || item.tipo === 'filme' || item.tipo === 'youtube';

  var embedUrl = item.youtubeId
    ? 'https://www.youtube.com/embed/' + item.youtubeId + '?autoplay=1&rel=0'
    : null;

  // Trava scroll do body (iOS Safari) e fecha com ESC
  useEffect(function() {
    var prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e) { if (e.key === 'Escape') onFechar(); }
    document.addEventListener('keydown', onKey);
    return function() {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onFechar]);

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px 16px',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        onClick={function(e) { e.stopPropagation(); }}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-xl)',
          width: '100%', maxWidth: 480,
          boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
        }}
      >
        {/* Botão fechar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px 0' }}>
          <button onClick={onFechar} style={{ background: 'var(--border)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Imagem / Player — portrait para livros, landscape para os demais */}
        {eMidia && embedUrl ? (
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, margin: '8px 16px 0' }}>
            <iframe
              src={embedUrl}
              title={item.titulo}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: 'var(--r-lg)' }}
            />
          </div>
        ) : item.imagem && !imgErro ? (
          item.tipo === 'livro' ? (
            /* Portrait — capa de livro */
            <div style={{ margin: '8px 16px 0', borderRadius: 'var(--r-lg)', background: cfg.cor, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px 0 16px' }}>
              <img src={item.imagem} alt={item.titulo} onError={function() { setImgErro(true); }}
                style={{ maxHeight: 210, maxWidth: '58%', width: 'auto', display: 'block', borderRadius: 4, boxShadow: '0 6px 24px rgba(0,0,0,0.28)' }} />
            </div>
          ) : (
            /* Landscape — produtos, filmes sem embed, etc */
            <div style={{ margin: '8px 16px 0', borderRadius: 'var(--r-lg)', overflow: 'hidden', background: cfg.cor }}>
              <img src={item.imagem} alt={item.titulo} onError={function() { setImgErro(true); }}
                style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 200 }} />
            </div>
          )
        ) : (
          <div style={{ margin: '8px 16px 0', borderRadius: 'var(--r-lg)', background: cfg.cor, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>
            {cfg.emoji}
          </div>
        )}

        {/* Conteúdo */}
        <div style={{ padding: '14px 20px 24px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {/* Pill principal: mostra a coleção (ex: "Hábitos") em vez do tipo genérico */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: '0.72rem',
              background: cfg.cor || '#eee', color: cfg.corTexto || '#333',
              borderRadius: 'var(--r-full)', padding: '3px 10px',
              fontWeight: 500, whiteSpace: 'nowrap',
            }}>
              {cfg.emoji} {item.colecao || cfg.label}
            </span>
            {item.nivel && <NivelPill nivel={item.nivel} />}
          </div>

          <h3 style={{ fontSize: '1.1rem', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: 'var(--primary)', lineHeight: 1.3, margin: '0 0 8px' }}>
            {item.titulo}
          </h3>

          {item.nota && (
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 16px', fontStyle: 'italic' }}>
              "{item.nota}"
            </p>
          )}

          {!eMidia && cfg.cta && temLink && (
            <a href={item.link} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', fontSize: '0.875rem', fontWeight: 600, color: '#fff', background: 'var(--primary)', borderRadius: 'var(--r-full)', padding: '10px 22px', textDecoration: 'none' }}>
              {cfg.cta} →
            </a>
          )}
          {eMidia && !embedUrl && temLink && (
            <a href={item.link} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', fontSize: '0.875rem', fontWeight: 600, color: '#fff', background: 'var(--primary)', borderRadius: 'var(--r-full)', padding: '10px 22px', textDecoration: 'none' }}>
              Assistir →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Card compacto para carrossel ──────────────────────────────────────────────
function CarouselCard({ item, onAbrir }) {
  var [imgErro, setImgErro] = useState(false);
  var cfg = TIPO_CONFIG[item.tipo] || {};
  var eMidia = item.tipo === 'meditacao' || item.tipo === 'filme' || item.tipo === 'youtube';
  var mostrarNivel = item.tipo === 'livro' && item.nivel;

  return (
    <div
      onClick={onAbrir}
      style={{ flexShrink: 0, width: 148, background: 'var(--surface)', borderRadius: 'var(--r-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
    >
      {/* Imagem */}
      <div style={{ height: 100, background: cfg.cor || '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
        {item.imagem && !imgErro ? (
          <img src={item.imagem} alt={item.titulo} onError={function() { setImgErro(true); }} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <span style={{ fontSize: '1.8rem' }}>{cfg.emoji}</span>
        )}
        {eMidia && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(47,43,95,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '8px 10px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <TipoPill tipo={item.tipo} small />
          {mostrarNivel && <NivelPill nivel={item.nivel} small />}
        </div>
        <p style={{ fontSize: '0.78rem', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, color: 'var(--primary)', lineHeight: 1.3, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {item.titulo}
        </p>
      </div>
    </div>
  );
}

// ── Seção Leitura do Mês ─────────────────────────────────────────────────────
function LeituraDoMes({ user }) {
  var [temLivro, setTemLivro] = useState(null); // null=carregando, false=sem livro, true=tem

  useEffect(function() {
    buscarLivroDoMes().then(function(livro) {
      setTemLivro(!!livro);
    });
  }, []);

  if (!temLivro) return null;

  var headingStyle = {
    fontSize: 22, fontFamily: "'Cormorant Garamond', Georgia, serif",
    color: 'var(--navy)', fontWeight: 400, letterSpacing: '-0.01em',
    margin: '0 0 14px', padding: '0 20px',
  };
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={headingStyle}>Leitura do Mês</h3>
      <div style={{ padding: '0 20px' }}>
        <LivroDoMes user={user} />
      </div>
    </div>
  );
}

// ── Carrossel de uma coleção ──────────────────────────────────────────────────
function ColecaoCarrossel({ colecao, onAbrirModal }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', marginBottom: 14 }}>
        <h3 style={{ fontSize: 22, fontFamily: "'Cormorant Garamond', Georgia, serif", color: 'var(--navy)', fontWeight: 400, letterSpacing: '-0.01em', margin: 0 }}>
          {colecao.nome}
        </h3>
      </div>
      <div style={{ display: 'flex', gap: 12, padding: '0 20px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {colecao.itens.map(function(item, i) {
          return (
            <CarouselCard
              key={i}
              item={item}
              onAbrir={function() { onAbrirModal(item); }}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Estado vazio ─────────────────────────────────────────────────────────────
function EstadoVazio({ emoji, mensagem }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>{emoji || '✨'}</div>
      <p style={{ fontSize: '0.95rem', lineHeight: 1.5, margin: 0 }}>{mensagem}</p>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ textAlign: 'center', padding: '48px 0' }}>
      <div className="loading-spinner" style={{ margin: '0 auto' }} />
    </div>
  );
}

function agruparComFallback(itens, labelFallback) {
  var comColecao = agruparPorColecao(itens);
  var semColecao = itens.filter(function(i) { return !i.colecao; });
  if (semColecao.length > 0) {
    comColecao = comColecao.concat([{ nome: labelFallback, itens: semColecao }]);
  }
  return comColecao;
}

// ── Aba: Tudo ────────────────────────────────────────────────────────────────
function AbaTudo({ itens, carregando, onAbrirModal, user }) {
  if (carregando) return <Spinner />;

  var colecoes = agruparEEmbaralhar(itens);

  return (
    <div style={{ paddingBottom: 32 }}>
      <div style={{ paddingTop: 20 }}>
        <LeituraDoMes user={user} />
        {colecoes.length === 0 && (
          <EstadoVazio emoji="✨" mensagem="Em breve — estamos preparando nossas primeiras recomendações para você." />
        )}
        {colecoes.map(function(colecao, i) {
          return <ColecaoCarrossel key={i} colecao={colecao} onAbrirModal={onAbrirModal} />;
        })}
      </div>
    </div>
  );
}

// ── Aba genérica em carrossel ─────────────────────────────────────────────────
function AbaCarrossel({ itens, carregando, labelFallback, vazioEmoji, vazioMensagem, onAbrirModal, mostrarLeitura, user }) {
  if (carregando) return <Spinner />;

  var colecoes = agruparComFallbackEmbaralhar(itens, labelFallback);

  if (!mostrarLeitura && colecoes.length === 0) {
    return <EstadoVazio emoji={vazioEmoji} mensagem={vazioMensagem} />;
  }

  return (
    <div style={{ paddingBottom: 32 }}>
      <div style={{ paddingTop: 20 }}>
        {mostrarLeitura && <LeituraDoMes user={user} />}
        {colecoes.length === 0 && mostrarLeitura && (
          <EstadoVazio emoji={vazioEmoji} mensagem={vazioMensagem} />
        )}
        {colecoes.map(function(colecao, i) {
          return <ColecaoCarrossel key={i} colecao={colecao} onAbrirModal={onAbrirModal} />;
        })}
      </div>
    </div>
  );
}

// ── Aba: Meditações ───────────────────────────────────────────────────────────
function AbaMeditacoes({ curadas, carregando, meditacoesLib, carregandoLib, onAbrirModal }) {
  var adaptadas = meditacoesLib.map(function(m) {
    return {
      titulo:    m.titulo,
      tipo:      'meditacao',
      nota:      m.descricao || '',
      imagem:    'https://img.youtube.com/vi/' + m.youtubeId + '/mqdefault.jpg',
      link:      'https://youtube.com/watch?v=' + m.youtubeId,
      youtubeId: m.youtubeId,
      nivel:     m.duracao || '',
      colecao:   m.categoria || 'Meditações',
    };
  });

  var libColecoes = agruparPorColecao(adaptadas);
  var curadaColecoes = agruparComFallback(curadas, 'Destaques');

  var headingStyle = {
    fontSize: 22, fontFamily: "'Cormorant Garamond', Georgia, serif",
    color: 'var(--navy)', fontWeight: 400, letterSpacing: '-0.01em',
    margin: '0 0 14px', padding: '0 20px',
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      {!carregando && curadas.length > 0 && (
        <div style={{ paddingTop: 20 }}>
          <h3 style={headingStyle}>Curadoria</h3>
          {curadaColecoes.map(function(col, i) {
            return <ColecaoCarrossel key={i} colecao={col} onAbrirModal={onAbrirModal} />;
          })}
          <div style={{ margin: '4px 20px 20px', borderTop: '1px solid var(--border)' }} />
        </div>
      )}
      {carregandoLib ? (
        <Spinner />
      ) : libColecoes.length === 0 ? (
        <EstadoVazio emoji="🧘" mensagem="Em breve — mais conteúdos chegando." />
      ) : (
        <div style={{ paddingTop: curadas.length > 0 ? 0 : 20 }}>
          <h3 style={headingStyle}>Biblioteca</h3>
          {libColecoes.map(function(col, i) {
            return <ColecaoCarrossel key={i} colecao={col} onAbrirModal={onAbrirModal} />;
          })}
        </div>
      )}
    </div>
  );
}

// ── Resultados de busca ───────────────────────────────────────────────────────
function BuscaResultados({ itens, meditacoesAdaptadas, query, onAbrirModal }) {
  var todos = itens.concat(meditacoesAdaptadas);
  var q = query.toLowerCase();
  var resultados = todos.filter(function(i) {
    return (i.titulo && i.titulo.toLowerCase().indexOf(q) !== -1)
      || (i.nota && i.nota.toLowerCase().indexOf(q) !== -1)
      || (i.colecao && i.colecao.toLowerCase().indexOf(q) !== -1);
  });

  if (resultados.length === 0) {
    return <EstadoVazio emoji="🔍" mensagem={'Nenhum resultado para "' + query + '".'} />;
  }

  return (
    <div style={{ paddingTop: 16 }}>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 8px', padding: '0 20px' }}>
        {resultados.length} resultado{resultados.length !== 1 ? 's' : ''}
      </p>
      <div style={{ display: 'flex', gap: 12, padding: '0 20px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
        {resultados.map(function(item, i) {
          return <CarouselCard key={i} item={item} onAbrir={function() { onAbrirModal(item); }} />;
        })}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ParaVocePage({ user, markParaVoceSeen }) {
  var [abaAtiva, setAbaAtiva] = useState('tudo');
  var [itens, setItens] = useState([]);
  var [config, setConfig] = useState({});
  var [carregando, setCarregando] = useState(true);
  var [meditacoesLib, setMeditacoesLib] = useState([]);
  var [carregandoLib, setCarregandoLib] = useState(true);
  var [buscaAtiva, setBuscaAtiva] = useState(false);
  var [busca, setBusca] = useState('');
  var [itemModal, setItemModal] = useState(null);

  var abrirModal = useCallback(function(item) { setItemModal(item); }, []);
  var fecharModal = useCallback(function() { setItemModal(null); }, []);

  useEffect(function() { markParaVoceSeen?.(); }, []); // eslint-disable-line

  useEffect(function() {
    buscarRecomendacoes(CSV_URL).then(function(resultado) {
      setItens(resultado.itens);
      setConfig(resultado.config);
      setCarregando(false);
    });
    buscarMeditacoes().then(function(dados) {
      setMeditacoesLib(dados);
      setCarregandoLib(false);
    });
  }, []);

  var meditacoesAdaptadas = meditacoesLib.map(function(m) {
    return {
      titulo:    m.titulo,
      tipo:      'meditacao',
      nota:      m.descricao || '',
      imagem:    'https://img.youtube.com/vi/' + m.youtubeId + '/mqdefault.jpg',
      link:      'https://youtube.com/watch?v=' + m.youtubeId,
      youtubeId: m.youtubeId,
      nivel:     m.duracao || '',
      colecao:   m.categoria || 'Meditações',
    };
  });

  var ABAS = [
    { id: 'tudo',       label: 'Tudo' },
    { id: 'livros',     label: 'Livros' },
    { id: 'produtos',   label: 'Produtos' },
    { id: 'assistir',   label: 'Para Assistir' },
    { id: 'meditacoes', label: 'Meditações' },
  ];

  var filtrados = {
    livros:   itens.filter(function(i) { return i.tipo === 'livro'; }),
    produtos: itens.filter(function(i) { return i.tipo === 'produto'; }),
    assistir: itens.filter(function(i) { return i.tipo === 'filme' || i.tipo === 'youtube'; }),
    curadas:  itens.filter(function(i) { return i.tipo === 'meditacao'; }),
  };

  var subtitulo = config.subtitulo || 'Curadoria de leituras, produtos e conteúdo para o seu bem-estar.';
  var buscaComQuery = buscaAtiva && busca.trim().length > 0;

  return (
    <div style={{ paddingBottom: 8 }}>
      {/* Modal */}
      {itemModal && <ItemModal item={itemModal} onFechar={fecharModal} />}

      {/* Cabeçalho */}
      <div style={{ padding: '24px 20px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, paddingRight: 12 }}>
          <h2 style={{ marginBottom: 4 }}>O Clube Recomenda</h2>
          {!buscaAtiva && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>{subtitulo}</p>
          )}
        </div>
        <button
          onClick={function() { if (buscaAtiva) { setBuscaAtiva(false); setBusca(''); } else { setBuscaAtiva(true); } }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-muted)', marginTop: 2, flexShrink: 0 }}
          aria-label={buscaAtiva ? 'Fechar busca' : 'Buscar'}
        >
          {buscaAtiva ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          )}
        </button>
      </div>

      {/* Campo de busca */}
      {buscaAtiva && (
        <div style={{ padding: '12px 20px 0' }}>
          <input
            autoFocus
            value={busca}
            onChange={function(e) { setBusca(e.target.value); }}
            placeholder="Buscar por título, tema ou coleção..."
            className="input"
            style={{ width: '100%', fontSize: '16px', boxSizing: 'border-box' }}
          />
        </div>
      )}

      {buscaComQuery ? (
        <BuscaResultados itens={itens} meditacoesAdaptadas={meditacoesAdaptadas} query={busca.trim()} onAbrirModal={abrirModal} />
      ) : (
        <>
          {/* Sub-abas */}
          {!buscaAtiva && (
            <div
              role="tablist"
              aria-label="Categorias"
              style={{ display: 'flex', gap: 8, padding: '16px 20px 0', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {ABAS.map(function(aba) {
                var ativo = aba.id === abaAtiva;
                return (
                  <button
                    key={aba.id}
                    role="tab"
                    aria-selected={ativo}
                    onClick={function() { setAbaAtiva(aba.id); }}
                    style={{
                      flexShrink: 0,
                      border: ativo ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                      borderRadius: 'var(--r-full)', padding: '7px 18px',
                      fontSize: '0.85rem', fontWeight: ativo ? 600 : 400,
                      background: ativo ? 'var(--primary)' : 'var(--surface)',
                      color: ativo ? '#fff' : 'var(--text-muted)',
                      cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}
                  >
                    {aba.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Conteúdo */}
          <div style={{ marginTop: 8 }}>
            {abaAtiva === 'tudo' && !buscaAtiva && (
              <AbaTudo itens={itens} carregando={carregando} onAbrirModal={abrirModal} user={user} />
            )}
            {abaAtiva === 'tudo' && buscaAtiva && (
              <EstadoVazio emoji="🔍" mensagem="Digite para buscar em todas as recomendações." />
            )}
            {abaAtiva === 'livros' && !buscaAtiva && (
              <AbaCarrossel
                itens={filtrados.livros} carregando={carregando}
                labelFallback="Leituras" mostrarLeitura user={user}
                vazioEmoji="📚" vazioMensagem="Em breve — selecionamos os melhores livros para sua jornada."
                onAbrirModal={abrirModal}
              />
            )}
            {abaAtiva === 'produtos' && !buscaAtiva && (
              <AbaCarrossel
                itens={filtrados.produtos} carregando={carregando}
                labelFallback="Produtos"
                vazioEmoji="🛍️" vazioMensagem="Em breve — produtos pensados para o seu autocuidado."
                onAbrirModal={abrirModal}
              />
            )}
            {abaAtiva === 'assistir' && !buscaAtiva && (
              <AbaCarrossel
                itens={filtrados.assistir} carregando={carregando}
                labelFallback="Para Assistir"
                vazioEmoji="🎬" vazioMensagem="Em breve — filmes e vídeos para inspirar sua jornada."
                onAbrirModal={abrirModal}
              />
            )}
            {abaAtiva === 'meditacoes' && !buscaAtiva && (
              <AbaMeditacoes
                curadas={filtrados.curadas} carregando={carregando}
                meditacoesLib={meditacoesLib} carregandoLib={carregandoLib}
                onAbrirModal={abrirModal}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
